/**
 * The pin reveal shared by Pin it and Next stop (DECISIONS 2026-10-03): the player's pin and the true spot as two
 * different markers, a line growing between them while the distance counts up, then the distance rings.
 * Instant with reduced motion (`host.animate`). Under 1 km there is no line: "Spot on".
 */
import { fmtKm, type LatLng } from "../game/geo";
import { distanceScore, PIN_RINGS_KM } from "../game/scoring";
import { along } from "../map/projection";
import type { Line, Pin, Ring } from "../map/MapView";
import type { Colors } from "../ui/theme";
import { t } from "../strings";

/** How long the line grows and the distance counts up. */
export const COUNT_MS = 800;
/** Closer than this, the pins sit on top of each other: no line, "Spot on". */
export const SPOT_ON_KM = 1;

export const spotOn = (km: number): boolean => km < SPOT_ON_KM;

/** The distance text in the verdict: "38 km off", or "Spot on". */
export const offText = (word: string, km: number): string => (spotOn(km) ? t.pin.spotOnVerdict(word) : t.pin.away(word, fmtKm(km)));

export interface DistanceReveal {
  guess: LatLng;
  /** The true spot's pin (colour, label, id); tagged "answer" here. */
  answer: Pin;
  km: number;
  /** 0–1: how far the line has grown and the distance has counted. Rings appear at 1. */
  t: number;
}

/** Map layers for the reveal at progress `r.t`. */
export function revealLayers(C: Colors, r: DistanceReveal): { pins: Pin[]; lines: Line[]; rings: Ring[] } {
  const near = spotOn(r.km);
  const pins: Pin[] = [
    { ...r.guess, kind: "guess", color: C.guess, z: 3, label: near ? t.pin.spotOn : t.pin.yourPin, tag: "guess" },
    { ...r.answer, z: 4, force: true, tag: "answer" },
  ];
  const lines: Line[] = near
    ? []
    : [{ pts: [[r.guess.lat, r.guess.lng], toPair(along(r.guess, r.answer, r.t))], color: C["ink-2"], width: 1.6, dash: true, label: fmtKm(r.km * r.t), tag: "distance" }];
  // Rings at the thresholds of the distance table (10 km = 20, 100 km = 10, 500 km = 5 points), once the count is done.
  const rings: Ring[] = r.t < 1 ? [] : PIN_RINGS_KM.map((d) => ({ lat: r.answer.lat, lng: r.answer.lng, km: d, label: t.pin.ring(distanceScore(d)), color: C.accent }));
  return { pins, lines, rings };
}

const toPair = (p: LatLng): [number, number] => [p.lat, p.lng];
